import { useMemo } from "react";
import { useCandidatesAddedReportQuery } from "../../../App/Queries/ReportingQueries";
import { getThemedColorCode } from "../../Helpers/ThemedColorCodes";
import { LegendType } from "recharts";
import { ICandidatesAddedDataPoint } from "../../../App/Typings/Reports/CandidatesAddedReport";
import _ from "lodash";

export interface ICandidatesAddedReportState {
  reportData: ICandidatesAddedDataPoint[];
  customLegendPayload: () => void;
  isLoading: boolean;
  numDaysInReport: number;
  startDate: Date;
  endDate: Date;
  isLoadingPrevReport: boolean;
  percentChange: number;
  prevEndDate: Date;
  prevStartDate: Date;
  previousCount: number;
}

export const useCandidatesAddedReportState = (startDate: Date, endDate: Date) => {
  const numDaysInReport = useMemo(() => {
    return Math.round((endDate.valueOf() - startDate.valueOf()) / (1000 * 60 * 60 * 24));
  }, [endDate, startDate]);

  const { data: reportData = [], isLoading } = useCandidatesAddedReportQuery(startDate, endDate);

  const { isLoadingPrevReport, percentChange, prevEndDate, prevStartDate, previousCount } =
    usePreviousReportData(startDate, endDate, reportData);

  return {
    reportData: reportData,
    customLegendPayload: () => {
      let totalCandidatesAdded = 0;
      for (const dataPoint of reportData) {
        totalCandidatesAdded += Number(dataPoint.candidateCount);
      }

      return [
        {
          dataKey: "candidateCount",
          color: getThemedColorCode(0),
          value: `Candidates: ${totalCandidatesAdded}`,
          type: "line" as LegendType,
        },
      ];
    },
    isLoading: isLoading || reportData.length === 0,
    isReportEnabled: true,
    numDaysInReport: numDaysInReport,
    startDate,
    endDate,
    isLoadingPrevReport,
    percentChange,
    prevEndDate,
    prevStartDate,
    previousCount,
  } as ICandidatesAddedReportState;
};

const usePreviousReportData = (startDate: Date, endDate: Date, reportData?: ICandidatesAddedDataPoint[]) => {
  const { prevStartDate, prevEndDate } = getPreviousTimespan(startDate, endDate);

  const { data: prevReportData, isLoading: isLoadingPrevReport } = useCandidatesAddedReportQuery(
    prevStartDate,
    prevEndDate,
  );

  const previousCount = prevReportData?.reduce((a, b) => a + b.candidateCount, 0) || 0;
  const percentChange = getPercentChange(prevReportData, reportData);

  return {
    isLoadingPrevReport,
    percentChange,
    prevEndDate,
    prevStartDate,
    previousCount,
  };
};

function getPercentChange(
  prev?: ICandidatesAddedDataPoint[] | null,
  current?: ICandidatesAddedDataPoint[] | null,
) {
  if (!prev || !current) {
    return 0;
  }
  const previousCount = prev.reduce((a, b) => a + b.candidateCount, 0);
  const currentCount = current.reduce((a, b) => a + b.candidateCount, 0);
  const percentChange = !previousCount
    ? currentCount * 100
    : ((currentCount - previousCount) / previousCount) * 100;
  return _.round(percentChange, 2);
}

function getPreviousTimespan(startDate: Date, endDate: Date) {
  const timespanInMilliseconds = endDate.valueOf() - startDate.valueOf();
  const prevStartDate = new Date(startDate.valueOf() - timespanInMilliseconds);
  const prevEndDate = new Date(endDate.valueOf() - timespanInMilliseconds);
  return { prevStartDate, prevEndDate };
}
